import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import useDesktop from './hooks/useDesktop';
import DesktopLayout from './components/DesktopLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider } from './lib/ThemeContext';
import { Toaster } from 'react-hot-toast';
import ThemeBackground from './components/ThemeBackground';
import NotificationPeek      from './components/NotificationPeek';
import IncomingCallOverlay  from './components/IncomingCallOverlay';
import FaceEnrollPrompt    from './components/FaceEnrollPrompt';
import GenderGate          from './components/GenderGate';
import PartnerGuard       from './components/PartnerGuard';
import SubAdminGuard          from './components/SubAdminGuard';
import BusinessGuard          from './components/BusinessGuard';
import StorageNotice  from './components/StorageNotice';
import { NavProvider, useNav } from './context/NavContext';
import API from './utils/api';
import useAndroidBack          from './hooks/useAndroidBack';

// --- Lazy-loaded pages (one chunk per route) ---
const Onboarding           = React.lazy(() => import('./pages/Onboarding'));
const Mosaic               = React.lazy(() => import('./pages/Mosaic'));
const PrivateChat          = React.lazy(() => import('./pages/PrivateChat'));
const MyProfile            = React.lazy(() => import('./pages/MyProfile'));
const Wallet               = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.Wallet })));
const Leaderboard          = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.Leaderboard })));
const Leaf                 = React.lazy(() => import('./pages/OtherPages').then(m => ({ default: m.Leaf })));
const Meets                = React.lazy(() => import('./pages/Meets'));
const CreateMeet           = React.lazy(() => import('./pages/CreateMeet'));
const MeetDetail           = React.lazy(() => import('./pages/MeetDetail'));
const MyMeets              = React.lazy(() => import('./pages/MyMeets'));
const BlindDateDiscover    = React.lazy(() => import('./pages/BlindDateDiscover'));
const BlindDateDetail      = React.lazy(() => import('./pages/BlindDateDetail'));
const BlindDateReveal      = React.lazy(() => import('./pages/BlindDateReveal'));
const BlindDateMy          = React.lazy(() => import('./pages/BlindDateMy'));
const VendorDashboard      = React.lazy(() => import('./pages/VendorDashboard'));
const PartnerEventDetail   = React.lazy(() => import('./pages/PartnerEventDetail'));
const PartnerDashboard     = React.lazy(() => import('./pages/PartnerDashboard'));
const PartnerCreateEvent   = React.lazy(() => import('./pages/PartnerCreateEvent'));
const AdminLayout          = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminBlindDates      = React.lazy(() => import('./pages/admin/AdminBlindDates'));
const AdminBlindDateDetail = React.lazy(() => import('./pages/admin/AdminBlindDateDetail'));
const AdminUsers           = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail      = React.lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminRefunds         = React.lazy(() => import('./pages/admin/AdminRefunds'));
const AdminPayments        = React.lazy(() => import('./pages/admin/AdminPayments'));
const AdminRevenue         = React.lazy(() => import('./pages/admin/AdminRevenue'));
const AdminAnalytics       = React.lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminBroadcast       = React.lazy(() => import('./pages/admin/AdminBroadcast'));
const AdminSettings        = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminMeets           = React.lazy(() => import('./pages/admin/AdminMeets'));
const AdminVendors         = React.lazy(() => import('./pages/admin/AdminVendors'));
const AdminTeam            = React.lazy(() => import('./pages/admin/AdminTeam'));
const AdminTeamActivity    = React.lazy(() => import('./pages/admin/AdminTeamActivity'));
const AdminPartnerEvents   = React.lazy(() => import('./pages/admin/AdminPartnerEvents'));
const BusinessLayout   = React.lazy(() => import('./pages/business/BusinessLayout'));
const BusinessOverview = React.lazy(() => import('./pages/business/BusinessOverview'));
const BusinessTrends   = React.lazy(() => import('./pages/business/BusinessTrends'));
const SubAdminLayout       = React.lazy(() => import('./pages/subadmin/SubAdminLayout'));
const SubAdminBlindDates   = React.lazy(() => import('./pages/subadmin/SubAdminBlindDates'));
const SubAdminRegistrations = React.lazy(() => import('./pages/subadmin/SubAdminRegistrations'));
const SubAdminMeets        = React.lazy(() => import('./pages/subadmin/SubAdminMeets'));
const SubAdminMeetMembers  = React.lazy(() => import('./pages/subadmin/SubAdminMeetMembers'));
const SubAdminSupportInbox = React.lazy(() => import('./pages/subadmin/SubAdminSupportInbox'));
const HelpPage             = React.lazy(() => import('./pages/HelpPage'));
const HelpTicketThread     = React.lazy(() => import('./pages/HelpTicketThread'));
const ProfileDetail        = React.lazy(() => import('./pages/ProfileDetail'));
const PrivacyPolicy  = React.lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/legal/TermsOfService'));
const RefundPolicy   = React.lazy(() => import('./pages/legal/RefundPolicy'));
const MyData         = React.lazy(() => import('./pages/legal/MyData'));

function PushOverlay() {
  const { currentPushed, pop } = useNav();
  if (!currentPushed) return null;
  if (currentPushed.screen === 'ProfileDetail') {
    return <ProfileDetail user={currentPushed.props.user} onBack={pop} />;
  }
  return null;
}

function AndroidBackHandler() {
  const { showExitDialog, setShowExitDialog, confirmExit } = useAndroidBack();
  if (!showExitDialog) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: 'var(--surface)', borderRadius: 16,
        padding: '24px 20px', width: '100%', maxWidth: 300,
        textAlign: 'center',
      }}>
        <h3 style={{ margin: '0 0 8px', fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
          Exit NxtLove?
        </h3>
        <p style={{ margin: '0 0 20px', color: 'var(--text2)', fontSize: '0.875rem' }}>
          Are you sure you want to exit the app?
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => setShowExitDialog(false)}
            style={{
              flex: 1, padding: '11px 0', borderRadius: '50px',
              background: 'var(--surface-2, var(--border))',
              border: '1px solid var(--border)', color: 'var(--text)',
              fontFamily: 'var(--font-body)', fontSize: '0.9rem',
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Stay
          </button>
          <button
            onClick={confirmExit}
            style={{
              flex: 1, padding: '11px 0', borderRadius: '50px',
              background: 'linear-gradient(135deg, #FF4E8A, #FF8C69)',
              border: 'none', color: '#fff',
              fontFamily: 'var(--font-body)', fontSize: '0.9rem',
              fontWeight: 700, cursor: 'pointer',
            }}
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}

const JoinRoute = () => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  if (!user) return <Onboarding />;
  if (user.isAdmin)  return <Navigate to="/admin" replace />;
  if (user.isSubAdmin && user.adminRole) return <Navigate to={`/subadmin/${user.adminRole}`} replace />;
  if (user.isVendor)  return <Navigate to="/vendor" replace />;
  if (user.isPartner) return <Navigate to="/partner" replace />;
  if (user.isBusinessViewer) return <Navigate to="/business" replace />;
  const openId = params.get('open');
  return <Navigate to={openId ? `/mosaic?open=${openId}` : '/mosaic'} replace />;
};

const Guard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',
      fontSize:'3rem'}}>🌳</div>
  );
  return user ? children : <Navigate to="/join" replace />;
};

const VendorGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',
      fontSize:'3rem'}}>🌳</div>
  );
  if (!user) return <Navigate to="/join" replace />;
  if (!user.isVendor) return <Navigate to="/" replace />;
  return children;
};

const AdminGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',
      fontSize:'3rem'}}>🌳</div>
  );
  if (!user) return <Navigate to="/join" replace />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return children;
};

const AppRoutes = () => {
  const { loading, user: authUser } = useAuth();
  const isDesktop   = useDesktop();
  const [maintenance, setMaintenance] = React.useState(null);

  React.useEffect(() => {
    API.get('/settings')
      .then(r => setMaintenance(r.data.maintenanceMode ? (r.data.maintenanceMsg || 'We\'ll be back shortly.') : false))
      .catch(() => setMaintenance(false));
  }, []);

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',
      height:'100vh',fontSize:'3rem'}}>🌳</div>
  );

  if (maintenance && !authUser?.isAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, padding: 24 }}>
        <div style={{ fontSize: '2rem' }}>🔧</div>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Maintenance</h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--text3)', textAlign: 'center', maxWidth: 300 }}>{maintenance}</p>
      </div>
    );
  }

  const suspenseFallback = (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',fontSize:'3rem'}}>🌳</div>
  );

  const routes = (
    <Suspense fallback={suspenseFallback}>
    <Routes>
      <Route path="/"              element={
        authUser?.isAdmin ? <Navigate to="/admin" replace /> :
        (authUser?.isSubAdmin && authUser.adminRole) ? <Navigate to={`/subadmin/${authUser.adminRole}`} replace /> :
        authUser?.isVendor ? <Navigate to="/vendor" replace /> :
        authUser?.isPartner ? <Navigate to="/partner" replace /> :
        authUser?.isBusinessViewer ? <Navigate to="/business" replace /> :
        <Navigate to="/mosaic" replace />
      } />
      <Route path="/join"          element={<JoinRoute />} />
      <Route path="/mosaic"        element={<Mosaic />} />
      <Route path="/leaf/:id"      element={<Leaf />} />
      <Route path="/chat/:roomId"  element={<Guard><PrivateChat /></Guard>} />
      <Route path="/profile/me"    element={<Guard><MyProfile /></Guard>} />
      <Route path="/wallet"        element={<Guard><Wallet /></Guard>} />
      <Route path="/leaderboard"   element={<Guard><Leaderboard /></Guard>} />
      <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
        <Route index                element={<Navigate to="blinddates" replace />} />
        <Route path="blinddates"       element={<AdminBlindDates />} />
        <Route path="blinddates/:id"   element={<AdminBlindDateDetail />} />
        <Route path="users"            element={<AdminUsers />} />
        <Route path="users/:uid"       element={<AdminUserDetail />} />
        <Route path="meets"            element={<AdminMeets />} />
        <Route path="vendors"          element={<AdminVendors />} />
        <Route path="refunds"          element={<AdminRefunds />} />
        <Route path="payments"         element={<AdminPayments />} />
        <Route path="revenue"          element={<AdminRevenue />} />
        <Route path="analytics"        element={<AdminAnalytics />} />
        <Route path="broadcast"        element={<AdminBroadcast />} />
        <Route path="settings"         element={<AdminSettings />} />
        <Route path="team"             element={<AdminTeam />} />
        <Route path="team/activity"    element={<AdminTeamActivity />} />
        <Route path="partner-events"   element={<AdminPartnerEvents />} />
      </Route>
      <Route path="/meets"         element={<Guard><Meets /></Guard>} />
      <Route path="/meets/:meetId" element={<MeetDetail />} />
      <Route path="/events/:id"    element={<PartnerEventDetail />} />
      <Route path="/partner"       element={<PartnerGuard><PartnerDashboard /></PartnerGuard>} />
      <Route path="/partner/events/create"   element={<PartnerGuard><PartnerCreateEvent /></PartnerGuard>} />
      <Route path="/partner/events/:id/edit" element={<PartnerGuard><PartnerCreateEvent /></PartnerGuard>} />
      <Route path="/business" element={<BusinessGuard><BusinessLayout /></BusinessGuard>}>
        <Route index    element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<BusinessOverview />} />
        <Route path="trends"   element={<BusinessTrends />} />
      </Route>
      <Route path="/blinddate"              element={<Guard><BlindDateDiscover /></Guard>} />
      <Route path="/blinddate/my"           element={<Guard><BlindDateMy /></Guard>} />
      <Route path="/blinddate/:id/reveal"   element={<Guard><BlindDateReveal /></Guard>} />
      <Route path="/blinddate/:id"          element={<BlindDateDetail />} />
      <Route path="/vendor"        element={<VendorGuard><VendorDashboard /></VendorGuard>} />

      {/* Blind Date sub-admin */}
      <Route path="/subadmin/blinddate" element={<SubAdminGuard role="blinddate"><SubAdminLayout role="blinddate" /></SubAdminGuard>}>
        <Route index element={<Navigate to="events" replace />} />
        <Route path="events"        element={<SubAdminBlindDates />} />
        <Route path="events/:id"    element={<AdminBlindDateDetail />} />
        <Route path="registrations" element={<SubAdminRegistrations />} />
      </Route>

      {/* Meet sub-admin */}
      <Route path="/subadmin/meet" element={<SubAdminGuard role="meet"><SubAdminLayout role="meet" /></SubAdminGuard>}>
        <Route index element={<Navigate to="meets" replace />} />
        <Route path="meets"              element={<SubAdminMeets />} />
        <Route path="meets/:id/members"  element={<SubAdminMeetMembers />} />
      </Route>

      {/* Support sub-admin */}
      <Route path="/subadmin/support" element={<SubAdminGuard role="support"><SubAdminLayout role="support" /></SubAdminGuard>}>
        <Route index element={<Navigate to="inbox" replace />} />
        <Route path="inbox" element={<SubAdminSupportInbox />} />
      </Route>

      {/* User Help */}
      <Route path="/help"                    element={<Guard><HelpPage /></Guard>} />
      <Route path="/help/ticket/:ticketId"   element={<Guard><HelpTicketThread /></Guard>} />

      {/* Legal — public, no auth required */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms"          element={<TermsOfService />} />
      <Route path="/refund-policy"  element={<RefundPolicy />} />
      <Route path="/my-data"        element={<Guard><MyData /></Guard>} />

      <Route path="*"              element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
  return isDesktop ? <DesktopLayout>{routes}</DesktopLayout> : routes;
};

export default function App() {
  return (
    <ThemeProvider>
      <ThemeBackground />
      <AuthProvider>
        <BrowserRouter>
          <SocketProvider>
            <NavProvider>
              <AppRoutes />
              <PushOverlay />
              <AndroidBackHandler />
            </NavProvider>
            <NotificationPeek />
            <IncomingCallOverlay />
            <FaceEnrollPrompt />
            <GenderGate />
            <StorageNotice />
            <Toaster position="top-center" toastOptions={{
              style: {
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border)',
                fontFamily: 'DM Sans,sans-serif', fontSize: '0.875rem',
              },
              success: { iconTheme: { primary: 'var(--accent)', secondary: 'var(--bg)' } },
            }} />
          </SocketProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
